/**
 * Bill display helpers.
 *
 * Two rules per the brief:
 *  - Support/Oppose badges MUST always carry the word plus the colour. Never
 *    colour alone — colour-blind users need the word, and screen readers pick
 *    up the value from the visible text.
 *  - Corners are 2px, not fully rounded pills. This is a document.
 */
import { CheckCircle2, XCircle, Pencil, Eye } from "lucide-react";

// Semantic token → Tailwind classes.
const POSITION_STYLES = {
  Support: {
    label: "Support",
    className:
      "bg-emerald-50 text-emerald-800 border border-emerald-700/30",
    // Legend-safe alternate for use on the plum field.
    onDarkClassName:
      "bg-emerald-50 text-emerald-900 border border-emerald-800",
    icon: CheckCircle2,
  },
  Oppose: {
    label: "Oppose",
    className:
      "bg-rose-50 text-rose-900 border border-rose-800/30",
    onDarkClassName:
      "bg-rose-50 text-rose-900 border border-rose-800",
    icon: XCircle,
  },
  Amend: {
    label: "Amend",
    className:
      "bg-amber-50 text-amber-900 border border-amber-800/30",
    onDarkClassName:
      "bg-amber-50 text-amber-900 border border-amber-800",
    icon: Pencil,
  },
  Monitor: {
    label: "Monitor",
    className:
      "bg-stone-100 text-stone-800 border border-stone-400",
    onDarkClassName:
      "bg-stone-100 text-stone-900 border border-stone-500",
    icon: Eye,
  },
};

/**
 * Legacy — returns just the classname portion so old callers keep working.
 * Prefer <PositionBadge /> below in new code so the word is guaranteed.
 */
export const positionBadge = (pos) => {
  const s = POSITION_STYLES[pos];
  return s ? s.className : POSITION_STYLES.Monitor.className;
};

/**
 * PositionBadge — colour + word + tiny leading glyph. The word is the
 * accessible source of truth; the colour is a rank-2 signal only.
 */
export function PositionBadge({ position, onDark = false, size = "sm" }) {
  const s = POSITION_STYLES[position] || POSITION_STYLES.Monitor;
  const Icon = s.icon;
  const sizeCls =
    size === "lg"
      ? "px-3 py-1 text-sm gap-1.5"
      : "px-2.5 py-0.5 text-xs gap-1";
  const className = onDark ? s.onDarkClassName : s.className;
  return (
    <span
      className={`inline-flex items-center ${sizeCls} font-semibold uppercase tracking-wider ${className}`}
      data-testid={`badge-${position?.toLowerCase() || "monitor"}`}
    >
      <Icon size={size === "lg" ? 14 : 12} aria-hidden="true" />
      <span>{s.label}</span>
    </span>
  );
}

export const STAGES = ["Introduced", "Committee", "Passed House", "Passed Senate", "Signed", "Vetoed"];

/**
 * StageStepper — the current status track for a bill card. Retinted to the
 * new palette (plum + rule + gold, no purple gradient). Vetoed shows a
 * plain word rule, not a coloured dot.
 */
export function StageStepper({ stage }) {
  const activeIdx = STAGES.indexOf(stage);
  const isVetoed = stage === "Vetoed";
  return (
    <div
      className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider"
      style={{ letterSpacing: "0.08em" }}
      data-testid="stage-stepper"
    >
      {STAGES.slice(0, 5).map((s, i) => {
        const done = i <= activeIdx && !isVetoed;
        return (
          <div key={s} className="flex items-center gap-1">
            <div
              className={`w-1.5 h-1.5 rounded-full ${
                done ? "bg-ccc-plum" : "bg-ccc-rule-strong"
              }`}
            />
            <span className={done ? "text-ccc-ink" : "text-ccc-muted"}>
              {s}
            </span>
            {i < 4 && (
              <div
                className={`w-3 h-px ${
                  done ? "bg-ccc-plum" : "bg-ccc-rule-strong"
                }`}
              />
            )}
          </div>
        );
      })}
      {isVetoed && (
        <span className="ml-2 text-ccc-oppose font-bold">— Vetoed</span>
      )}
    </div>
  );
}
